// compare_bars — duas barras crescem lado a lado (a da direita na cor de destaque), os
// valores contam em cima e os rótulos entram embaixo.
import React from 'react';
import { interpolate } from 'remotion';
import type { CompareBarsProps } from './schema';
import { formatGraphicNumber } from './schema';
import { CARD, EASE_BACK, EASE_OUT, MUTED, Pop, SAFE_W, alpha, fitFont, prog, useGFrame, type GraphicComponentProps } from './common';

const W = SAFE_W - 60;
const BAR_W = 190;
const MAX_H = 220;

const isCurrency = (u: string) => /^(r\$|us\$|\$|€|£)$/i.test(u.trim());

export const CompareBars: React.FC<GraphicComponentProps<CompareBarsProps>> = ({ props, totalFrames, accent }) => {
  const f = useGFrame();
  const unit = props.unit || '';
  const max = Math.max(props.left.value, props.right.value) || 1;
  const decimals = [props.left.value, props.right.value].some((v) => !Number.isInteger(v)) ? 1 : 0;
  const growEnd = Math.max(14, Math.min(26, Math.round(totalFrames * 0.45)));
  const bars = [
    { ...props.left, t0: 5, color: 'linear-gradient(180deg,rgba(255,255,255,0.42),rgba(255,255,255,0.2))', glow: 'none', valueColor: '#fff' },
    { ...props.right, t0: 10, color: `linear-gradient(180deg, ${accent}, ${alpha(accent, 0.72)})`, glow: `0 0 28px ${alpha(accent, 0.45)}`, valueColor: '#fff' },
  ];
  const fmt = (v: number) => {
    const n = formatGraphicNumber(v, decimals);
    if (!unit) return n;
    return isCurrency(unit) ? `${unit} ${n}` : `${n}${unit.length <= 2 ? '' : ' '}${unit}`;
  };
  const valueSize = fitFont(fmt(max), { weight: 900, max: 56, min: 36, maxWidth: BAR_W + 60 });
  const labelSize = fitFont([props.left.label, props.right.label].sort((a, b) => b.length - a.length)[0], { weight: 700, max: 38, min: 28, maxWidth: BAR_W + 90 });

  return (
    <div style={{ ...CARD, width: W, padding: `${props.title ? 32 : 40}px 50px 34px` }}>
      {props.title ? (
        <div style={{ textAlign: 'center', fontWeight: 800, fontSize: 38, color: '#fff', marginBottom: 18, letterSpacing: -0.3 }}>{props.title}</div>
      ) : null}
      <div style={{ position: 'relative', height: MAX_H + valueSize * 1.3 + 8, display: 'flex', justifyContent: 'center', alignItems: 'flex-end', gap: 110 }}>
        {bars.map((b, i) => {
          const g = prog(f, b.t0, b.t0 + growEnd - 4, EASE_BACK);
          const h = Math.max(14, (b.value / max) * MAX_H) * Math.max(0, g);
          const countP = prog(f, b.t0, b.t0 + growEnd - 4, EASE_OUT);
          const vIn = prog(f, b.t0 + 2, b.t0 + 8, EASE_OUT);
          return (
            <div key={i} style={{ width: BAR_W, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end' }}>
              <Pop at={b.t0 + growEnd - 4} volume={0.1} />
              <div
                style={{
                  fontWeight: 900,
                  fontSize: valueSize,
                  lineHeight: 1.1,
                  color: b.valueColor,
                  marginBottom: 10,
                  whiteSpace: 'nowrap',
                  opacity: vIn,
                  filter: `blur(${((1 - vIn) * 6).toFixed(2)}px)`,
                  letterSpacing: -1,
                }}
              >
                {fmt(b.value * countP)}
              </div>
              <div style={{ width: BAR_W, height: h, borderRadius: '18px 18px 6px 6px', background: b.color, boxShadow: b.glow }} />
            </div>
          );
        })}
      </div>
      <div style={{ height: 4, borderRadius: 3, background: 'rgba(255,255,255,0.14)', margin: '0 -10px' }} />
      <div style={{ display: 'flex', justifyContent: 'center', gap: 110, marginTop: 16 }}>
        {bars.map((b, i) => {
          const lIn = prog(f, b.t0 + 4, b.t0 + 11, EASE_OUT);
          return (
            <div
              key={i}
              style={{
                width: BAR_W + 100,
                margin: '0 -50px',
                textAlign: 'center',
                fontWeight: 700,
                fontSize: labelSize,
                lineHeight: 1.1,
                color: i === 1 ? '#fff' : MUTED,
                opacity: lIn,
                translate: `0px ${interpolate(lIn, [0, 1], [10, 0]).toFixed(1)}px`,
              }}
            >
              {b.label}
            </div>
          );
        })}
      </div>
    </div>
  );
};
